import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { TimeDuration, TimeFrame } from "~/types";
import { durationToDayjs } from "./constants";

// TODO [Translate]

dayjs.extend(customParseFormat);

// Parse formats
const dayP = "DD-MM-YYYY";
const timeP = "HH:mm";
const dayTimeP = `${dayP} ${timeP}`;

// Display formats
const timeF = "h:mm\u00A0A";
const dayF = "Do";
const monthShortF = "MMM";
const monthLongF = "MMMM";
const yearF = "YYYY";

const dayTimeF = `${dayF}, ${timeF}`;
const monthDayTimeF = `${monthShortF} ${dayTimeF}`;
const monthDayYearTimeF = `${monthShortF} ${dayF}, ${yearF}; ${timeF}`;

export const addDurationTo = ({
  date,
  duration,
}: {
  date?: dayjs.Dayjs;
  duration?: TimeDuration;
}) => {
  if (!date) date = dayjs();
  if (!duration) return date;
  return date.add(duration.num, durationToDayjs(duration.duration));
};

export const mergeDateAndTime = (date: dayjs.Dayjs, time: dayjs.Dayjs) => {
  return parseDate(date.format(dayP), time.format(timeP));
};

export const parseDayTime = (dayTime: string): dayjs.Dayjs => {
  return dayjs(dayTime, dayTimeP);
};

export const parseDate = (day: string, time: string): dayjs.Dayjs => {
  return dayjs(`${day} ${time}`, "DD-MM-YYYY HH:mm");
};

export const parseTime = (time: string): dayjs.Dayjs => {
  return dayjs(time, timeP);
};

/** Comparator for Ant Design table sorters; empty dates sort last. */
export const compareIsoDates = (
  dateA?: string | null,
  dateB?: string | null,
) => {
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;
  return new Date(dateA).getTime() - new Date(dateB).getTime();
};

export const timeIsInRange = (
  timeFrame: TimeFrame,
  time: dayjs.Dayjs,
): boolean => {
  return timeFrame.start.isBefore(time) && timeFrame.end.isAfter(time);
};

export const simpleDateFormat = (date: dayjs.Dayjs): string => {
  return dateFormat(date, "D MMM YYYY; h:mm a");
};

export const dateFormat = (date: dayjs.Dayjs, format: string): string => {
  return date.format(format);
};

export const timeFrameTimeFormat = (timeFrame: TimeFrame): string =>
  timeFrameFormat(timeFrame, timeF, true);

const timeFrameFormats = (
  timeFrame: TimeFrame,
  formatStart: string,
  formatEnd: string,
  nonBreak?: boolean,
): string => {
  if (nonBreak)
    return `${timeFrame.start.format(
      formatStart,
    )}\u00A0\u2015\u00A0${timeFrame.end.format(formatEnd)}`;
  return `${timeFrame.start.format(formatStart)} \u2015 ${timeFrame.end.format(
    formatEnd,
  )}`;
};

const timeFrameFormat = (
  timeFrame: TimeFrame,
  format: string,
  nonBreak?: boolean,
) => {
  return timeFrameFormats(timeFrame, format, format, nonBreak);
};

// Will give a string that represents the time frame, but will not
// print day, month, or year twice if they are the same.
// (Will also not print the year if it is the current year).
export const sparseTimeFrameFormat = (timeFrame: TimeFrame): string => {
  const now = dayjs();
  if (timeFrame.start.year() !== timeFrame.end.year()) {
    return timeFrameFormat(timeFrame, monthDayYearTimeF);
  }
  // don't show year if it is this year.
  if (now.year() === timeFrame.end.year()) {
    if (timeFrame.start.month() !== timeFrame.end.month()) {
      return timeFrameFormat(timeFrame, monthDayTimeF);
    }
    if (timeFrame.start.day() !== timeFrame.end.day()) {
      return timeFrameFormats(
        timeFrame,
        `${monthLongF}; ${dayTimeF}`,
        dayTimeF,
      );
    }
    return timeFrameFormats(
      timeFrame,
      `${monthShortF} ${dayF}; ${timeF}`,
      timeF,
    );
  }

  if (timeFrame.start.month() !== timeFrame.end.month()) {
    return timeFrameFormats(
      timeFrame,
      `${yearF}; ${monthDayTimeF}`,
      monthDayTimeF,
    );
  }
  if (timeFrame.start.day() !== timeFrame.end.day()) {
    return timeFrameFormats(
      timeFrame,
      `${monthLongF} ${yearF}; ${dayTimeF}`,
      dayTimeF,
    );
  }
  return timeFrameFormats(timeFrame, monthDayYearTimeF, timeF);
};

/**
 * This function compares two input time strings.
 * @param time1 The first time string to compare, should be in the format hh:mm where hh are the hours and mm are the minutes.
 * @param time2 The second time string to compare, should be in the format hh:mm where hh are the hours and mm are the minutes.
 * @returns 1 if the first time string is earlier than the second,
 * -1 if the second is earlier than the first, and 0 if they are the same time.
 */
export const CompareTimeStrings = (time1: string, time2: string): number => {
  const startHour1 = Number(time1.split(":")[0]);
  const startMinute1 = Number(time1.split(":")[1].split(" ")[0]);
  const startHour2 = Number(time2.split(":")[0]);
  const startMinute2 = Number(time2.split(":")[1].split(" ")[0]);
  if (
    startHour1 > startHour2 ||
    (startHour1 === startHour2 && startMinute1 > startMinute2)
  ) {
    return 1;
  } else if (
    startHour1 < startHour2 ||
    (startHour1 === startHour2 && startMinute1 < startMinute2)
  ) {
    return -1;
  } else {
    return 0;
  }
};

/**
 * This function takes in an array of time strings, sorts them earliest to latest, and concatenates them into a single comma-separated string.
 * @param hours an array of time strings, each time string should be in the format should be in the format hh:mm where hh are the hours and mm are the minutes.
 * @returns a single string of all the times, or "CLOSED" if the given array is empty.
 */
export const OrderHoursAndJoin = (hours: string[]): string => {
  if (hours.length > 0) {
    hours.sort(CompareTimeStrings);
    return hours.join(", ");
  } else {
    return "CLOSED"; // TODO [Translate]
  }
};

// TODO: fix this function to use the correct language
export const dateToLocalTime = (
  utcDateString: string,
  format: "date" | "dateWithDay" | "time" | "dateTime" | "all",
  intlLocale: Intl.LocalesArgument = "en-US",
): string => {
  if (utcDateString === "") return "";
  const date = new Date(utcDateString);
  let language: Intl.LocalesArgument = intlLocale;

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    weekday: format === "dateWithDay" || format === "all" ? "short" : undefined,
    hour:
      format === "time" || format === "dateTime" || format === "all"
        ? "2-digit"
        : undefined,
    minute:
      format === "time" || format === "dateTime" || format === "all"
        ? "2-digit"
        : undefined,
    second: format === "time" || format === "all" ? "2-digit" : undefined,
    timeZoneName: format === "all" ? "short" : undefined,
  };

  return date.toLocaleString(language, options);
};

/** Maps i18next `lng` from the tenant app to `Intl` locales. */
const APP_LANG_TO_INTL_LOCALE: Record<string, Intl.LocalesArgument> = {
  eng: "en-US",
  fr: "fr-FR",
  es: "es",
};

/**
 * Formats API date strings (ISO, YYYY-MM-DD, etc.) for read-only UI.
 * Uses the app language when provided (matches `dateToLocalTime` date style).
 */
export function formatDisplayDateOnly(
  value: string | null | undefined,
  appLanguage?: string
): string {
  if (value == null || String(value).trim() === "") {
    return "—";
  }
  const d = dayjs(String(value).trim());
  if (!d.isValid()) {
    return "—";
  }
  const locale =
    (appLanguage && APP_LANG_TO_INTL_LOCALE[appLanguage]) ?? "en-US";
  return d.toDate().toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Label for a rent payment period option.
 * Prefers coverage dates from the rent-cycle engine when present so anniversary
 * / prorated stubs are distinguishable from calendar month labels alone.
 */
export function formatRentPaymentPeriodLabel(params: {
  month: number;
  year: number;
  periodStart?: string | null;
  periodEnd?: string | null;
  monthWord?: string;
  yearWord?: string;
  appLanguage?: string;
}): string {
  const monthWord = params.monthWord ?? "Month";
  const yearWord = params.yearWord ?? "Year";
  const billing = `${monthWord}: ${params.month + 1} ${yearWord}: ${params.year}`;
  if (!params.periodStart || !params.periodEnd) {
    return billing;
  }
  const start = formatDisplayDateOnly(params.periodStart, params.appLanguage);
  const end = formatDisplayDateOnly(params.periodEnd, params.appLanguage);
  if (start === "—" || end === "—") {
    return billing;
  }
  return `${billing} (${start} – ${end})`;
}

/**
 * True when coverage is a partial calendar-month stub (prorated first/last month).
 * Anniversary windows that cross months are not treated as prorated stubs.
 */
export function isPartialRentCoveragePeriod(
  periodStart?: string | null,
  periodEnd?: string | null,
): boolean {
  if (!periodStart || !periodEnd) return false;
  const start = dayjs(String(periodStart).trim());
  const end = dayjs(String(periodEnd).trim());
  if (!start.isValid() || !end.isValid()) return false;
  if (!start.isSame(end, "month")) return false;
  const monthStart = start.startOf("month");
  const monthEnd = start.endOf("month");
  return !start.isSame(monthStart, "day") || !end.isSame(monthEnd, "day");
}

/**
 * Generates a list of month-year pairs for rent computation between two dates.
 * For rent purposes, we include the start month and the end month if the end date
 * falls within that month.
 *
 * @param startDate - ISO string of the start date
 * @param endDate - ISO string of the end date
 * @returns Array of objects containing month and year
 */
export const getRentMonths = (
  startDate: string,
  endDate: string,
): Array<{ month: number; year: number }> => {
  if (startDate === "" || endDate === "") {
    return [];
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  // Validate dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    console.error("Invalid date format in getRentMonths:", {
      startDate,
      endDate,
    });
    return [];
  }

  if (start > end) {
    console.error("Start date must be before end date in getRentMonths:", {
      startDate,
      endDate,
    });
    return [];
  }

  // Return empty list if start and end dates are the same
  if (start.getTime() === end.getTime()) {
    return [];
  }

  const months: Array<{ month: number; year: number }> = [];

  // Use UTC methods to avoid timezone issues
  const startYear = start.getUTCFullYear();
  const startMonth = start.getUTCMonth();

  const endYear = end.getUTCFullYear();
  const endMonth = end.getUTCMonth();

  // Start from the first day of the start month
  let currentYear = startYear;
  let currentMonth = startMonth;

  // Include start month and end month
  while (
    currentYear < endYear ||
    (currentYear === endYear && currentMonth <= endMonth)
  ) {
    months.push({
      month: currentMonth, // JavaScript months are 0-based and we will keep it that way in the database
      year: currentYear,
    });

    // Move to next month
    currentMonth++;
    if (currentMonth > 11) {
      // 11 is December
      currentMonth = 0;
      currentYear++;
    }
  }

  return months;
};
